import { EllipsisHorizontalIcon, PencilIcon } from '@heroicons/react/20/solid'
import { CheckIcon } from '@radix-ui/react-icons'
import { FormikProps, useFormikContext } from 'formik'
import React from 'react'
import {
  ProjectFormEnum,
  projectFormEnumToProjectFormCategory,
  projectFormEnumToProjectFormType,
  TechnicalFormStatusEnum,
} from 'shared'
import { Button } from '../../../../components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../../components/ui/dropdown-menu'
import { useDeletePreviewProjectTechnicalFormMutation } from '../../../../model/api/project/useDeletePreviewProjectTechnicalFormMutation'
import { useSubmitForPreviewProjectFormMutation } from '../../../../model/api/project/useSubmitForPreviewProjectFormMutation'
import { useGenerateReportAction } from '../../../../model/hooks/useGenerateReportAction'

import { DocumentCheckIcon, DocumentTextIcon } from '@heroicons/react/24/outline'
import { Badge } from '../../../../components/ui/badge'

interface Props {
  projectId: string
  status: TechnicalFormStatusEnum
  projectForm: ProjectFormEnum
}

export const AccompanyingReportMenu: React.FC<Props> = (props) => {
  const projectFormType = projectFormEnumToProjectFormType(props.projectForm)
  const { isLoading: isDownloadingPdf, onDownload } = useGenerateReportAction(
    props.projectId,
    projectFormType,
  )
  const { isLoading: isDownloadingDocx, onDownload: onDownloadDocx } = useGenerateReportAction(
    props.projectId,
    projectFormType,
    'docx',
  )
  const isDownloading = isDownloadingPdf || isDownloadingDocx
  const { mutate: submitForPreviewProjectForm } = useSubmitForPreviewProjectFormMutation()
  const { mutate: deletePreviewProjectTechnicalForm } =
    useDeletePreviewProjectTechnicalFormMutation()
  const formik = useFormikContext() as FormikProps<any>

  const submitForPreview = async () => {
    await formik.submitForm()
    setTimeout(() => {
      submitForPreviewProjectForm({
        id: props.projectId,
        form: props.projectForm as any,
      })
    }, 500)
  }
  const revertFromPreview = () => {
    deletePreviewProjectTechnicalForm({
      id: props.projectId,
      form: props.projectForm as any,
    })
  }

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'e') {
        event.preventDefault()
        revertFromPreview()
      }

      if ((event.ctrlKey || event.metaKey) && event.key === 'd') {
        event.preventDefault()
        submitForPreview()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" type="button" className="ml-1">
          <EllipsisHorizontalIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56">
        <DropdownMenuLabel>Menu</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem
            disabled={props.status === TechnicalFormStatusEnum.PREVIEW}
            onClick={submitForPreview}
          >
            <CheckIcon className="mr-2 h-4 w-4" />
            <div className="flex justify-between w-full">
              <span>Dokončit</span>
              <Badge variant="secondary" className="ml-2">
                CTRL+D
              </Badge>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={props.status !== TechnicalFormStatusEnum.PREVIEW}
            onClick={revertFromPreview}
          >
            <PencilIcon className="mr-2 h-4 w-4" />
            <div className="flex justify-between w-full">
              <span>Editovat</span>
              <Badge variant="secondary" className="ml-2">
                CTRL+E
              </Badge>
            </div>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem
            disabled={props.status === TechnicalFormStatusEnum.EDIT || isDownloading}
            onClick={onDownload}
          >
            <DocumentCheckIcon className="mr-2 h-4 w-4" />
            <span>Stáhnout výstup (.pdf)</span>
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={props.status === TechnicalFormStatusEnum.EDIT || isDownloading}
            onClick={onDownloadDocx}
          >
            <DocumentTextIcon className="mr-2 h-4 w-4" />
            <span>Stáhnout výstup (.docx)</span>
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
